import { MemoExoticComponent } from 'react'

import { TGenericAPIError } from '~/app/models'
import { EConversationChannel } from '~/models/features'

export enum EScenarioKeys {
  id = 'id',
  name = 'name',
  triggerInstructions = 'triggerInstructions',
  additionalInstructions = 'additionalInstructions',
  agentCustomFields = 'agentCustomFields',
  actionIds = 'actionIds',
  channels = 'channels',
  instructionSteps = 'instructionSteps',
  instructionStep = 'InstructionStep',
  activeAgentIds = 'activeAgentIds',
  availableForAgentIds = 'availableForAgentIds',
  updatedDate = 'updatedDate',
}

export enum EScenarioCalStepPageKeys {
  apiKey = 'apiKey',
  eventTypeId = 'eventTypeId',
}

export enum EScenarioStepPagesKeys {
  cal = 'cal',
}

export type TScenarioCalStepPage = {
  stepType: EScenarioStepPagesKeys.cal
}

// Add other customs steps, example: TScenarioCalStepPage | TNewCustomStep
export type TScenarioStepPages = TScenarioCalStepPage

export type TInstructionStep = {
  content: string
  id: string
  scenarioId: string
  stepNumber: number
}

export type TScenarioPayload = {
  [EScenarioKeys.name]: string
  [EScenarioKeys.instructionSteps]?: Record<number, string>
  [EScenarioKeys.instructionStep]?: TInstructionStep[]
  [EScenarioKeys.triggerInstructions]?: string
  [EScenarioKeys.additionalInstructions]?: string
  [EScenarioKeys.agentCustomFields]?: Record<string, TAgentCustomField>
  [EScenarioKeys.actionIds]?: string[]
  [EScenarioKeys.channels]?: EChannelKeys[]
}

export type TUpdateScenarioTitlePayload = {
  [EScenarioKeys.name]: string
}

export enum EFormAgentCustomFieldKeys {
  key = 'key',
  type = 'type',
  description = 'description',
  required = 'required',
}

export type TFormAgentCustomField = TAgentCustomField & {
  [EFormAgentCustomFieldKeys.key]: string
}

export type TUpdateScenarioPayload = Partial<
  Omit<
    TScenarioPayload,
    | EScenarioKeys.name
    | EScenarioKeys.instructionSteps
    | EScenarioKeys.agentCustomFields
  > & {
    instructionSteps: string
    agentCustomFields: TFormAgentCustomField[]
  }
>

export type TScenario = {
  [EScenarioKeys.id]: string
  [EScenarioKeys.activeAgentIds]?: string[]
  [EScenarioKeys.availableForAgentIds]?: string[]
  [EScenarioKeys.updatedDate]?: string
} & Omit<TScenarioPayload, EScenarioKeys.instructionSteps>

export type TScenarioError = TGenericAPIError

export enum EActionKeys {
  id = 'id',
  name = 'name',
  parametersList = 'parametersList',
  waitMessage = 'waitMessage',
  allPropertiesRequired = 'allPropertiesRequired',
  useTemplate = 'useTemplate',
  webhook = 'webhook',
  description = 'description',
  scenarioTemplateActionId = 'scenarioTemplateActionId',
  updatedDate = 'updatedDate',
  httpMethod = 'httpMethod',
  authHeader = 'authHeader',
  apiKey = 'apiKey',
  failureMessage = 'failureMessage',
  headers = 'headers',
  connectionId = 'connectionId',
}

export type TScenarioAgentCustomField = string | boolean | number

export type TScenarioAgentCustomFields = // the metadata table.
  Record<string, TScenarioAgentCustomField>

export type TAgentCustomField = {
  [EFormAgentCustomFieldKeys.type]: TScenarioAgentCustomField
  [EFormAgentCustomFieldKeys.description]: string
  [EFormAgentCustomFieldKeys.required]: boolean
}

export type TAction = {
  [EActionKeys.id]: string
  [EActionKeys.name]: string
  [EActionKeys.parametersList]: Record<string, TParameterListContent>
  [EActionKeys.waitMessage]: string
  [EActionKeys.allPropertiesRequired]: boolean
  [EActionKeys.useTemplate]: string
  [EActionKeys.webhook]: string
  [EActionKeys.description]: string
  [EActionKeys.scenarioTemplateActionId]?: string | null
  [EActionKeys.updatedDate]?: string | null
  [EActionKeys.httpMethod]: EScenarioActionMethod
  [EActionKeys.authHeader]?: string | null
  [EActionKeys.apiKey]?: string | null
  [EActionKeys.failureMessage]?: string | null
  [EActionKeys.headers]?: string | null
  [EActionKeys.connectionId]?: string | null
}

export enum EChannelKeys {
  phone = 'phone',
  email = 'email',
  text = 'text',
  chat = 'chat',
}

export const ChannelFriendlyNames: Record<EChannelKeys, string> = {
  [EChannelKeys.phone]: 'Phone',
  [EChannelKeys.email]: 'Email',
  [EChannelKeys.text]: 'Text',
  [EChannelKeys.chat]: 'Chat',
}

export enum EScenarioAgentKeys {
  id = 'id',
  scenarioId = 'scenarioId',
  createdDate = 'createdDate',
  updatedDate = 'updatedDate',
  active = 'active',
  agentId = 'agentId',
  customFields = 'customFields',
  timezone = 'timezone',
  Agent = 'Agent',
}

export type TScenarioAgentAgent = {
  agentReferenceName: string
}

export type TScenarioAgent = {
  [EScenarioAgentKeys.id]: string
  [EScenarioAgentKeys.scenarioId]: string
  [EScenarioAgentKeys.createdDate]: string
  [EScenarioAgentKeys.updatedDate]: string
  [EScenarioAgentKeys.active]: boolean
  [EScenarioAgentKeys.agentId]: string
  [EScenarioAgentKeys.customFields]: TScenarioAgentCustomFields | null
  [EScenarioAgentKeys.timezone]: string | null
  [EScenarioAgentKeys.Agent]: TScenarioAgentAgent | null
}

export type TUpdateScenarioAgentPayload = Omit<
  TScenarioAgent,
  | EScenarioAgentKeys.id
  | EScenarioAgentKeys.agentId
  | EScenarioAgentKeys.scenarioId
  | EScenarioAgentKeys.createdDate
  | EScenarioAgentKeys.updatedDate
  | EScenarioAgentKeys.Agent
>

export enum EScenarioActionKeys {
  id = 'id',
  name = 'name',
  createdDate = 'createdDate',
  updatedDate = 'updatedDate',
  parametersList = 'parametersList',
  waitMessage = 'waitMessage',
  allPropertiesRequired = 'allPropertiesRequired',
  usesTemplate = 'usesTemplate',
  webhook = 'webhook',
  description = 'description',
  failureMessage = 'failureMessage',
  method = 'httpMethod',
  authHeader = 'authHeader',
  apiKey = 'apiKey',
  apiCredentialId = 'apiCredentialId',
  connectionId = 'connectionId',
  headers = 'headers',
  actionIds = 'actionIds',
  apiActionDescription = 'apiActionDescription',
  scenarioTemplateActionId = 'scenarioTemplateActionId',
}

export enum EScenarioActionUsesTemplate {
  YES = 'yes',
  NO = 'no',
}

export const PARAMETER_LIST_PRIMITIVE_TYPES: TParameterListPrimitiveType[] = [
  'string',
  'number',
  'boolean',
] as const

type TParameterListPrimitiveType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'object'
  | 'array'

export type TParameterListType =
  | TParameterListPrimitiveType
  | [TParameterListPrimitiveType, 'null']

export enum EParameterListKeys {
  type = 'type',
  description = 'description',
  required = 'required',
  properties = 'properties',
  name = 'name',
  isPathParameter = 'isPathParameter',
}

export type TParameterListContent = {
  [EParameterListKeys.type]: TParameterListType
  [EParameterListKeys.description]: string
  [EParameterListKeys.required]: boolean
  [EParameterListKeys.properties]?:
    | Record<string, TParameterListContent>
    | ({ [EParameterListKeys.name]: string } & Omit<
        TParameterListContent,
        EParameterListKeys.properties
      >)[]
  [EParameterListKeys.isPathParameter]?: boolean
}

export enum EScenarioActionMethod {
  POST = 'POST',
  GET = 'GET',
  DELETE = 'DELETE',
  PUT = 'PUT',
}

export type TScenarioActionPayload = {
  [EScenarioActionKeys.name]: string
  [EScenarioActionKeys.parametersList]: Record<string, TParameterListContent>
  [EScenarioActionKeys.waitMessage]: string
  [EScenarioActionKeys.allPropertiesRequired]: boolean
  [EScenarioActionKeys.usesTemplate]: EScenarioActionUsesTemplate
  [EScenarioActionKeys.webhook]: string
  [EScenarioActionKeys.description]: string
  [EScenarioActionKeys.failureMessage]: string
  [EScenarioActionKeys.method]?: EScenarioActionMethod
  [EScenarioActionKeys.apiCredentialId]?: string
  [EScenarioActionKeys.connectionId]?: string | null
  [EScenarioActionKeys.headers]?: string
  [EScenarioActionKeys.scenarioTemplateActionId]?: string
}

export enum EFormParameterListKeys {
  id = 'id',
  name = 'name',
  description = 'description',
  type = 'type',
  isOptional = 'isOptional',
  properties = 'properties',
  isPathParameter = 'isPathParameter',
}

export type TFormParameterListType = TParameterListPrimitiveType

export type TFormParameterListContentBase = {
  [EFormParameterListKeys.id]: string
  [EFormParameterListKeys.name]: string
  [EFormParameterListKeys.description]: string
  [EFormParameterListKeys.type]: TFormParameterListType
  [EFormParameterListKeys.isOptional]: boolean
}

export type TFormParameterListContent = TFormParameterListContentBase & {
  [EFormParameterListKeys.properties]?: TFormParameterListContentBase[]
  [EFormParameterListKeys.isPathParameter]?: boolean
}

export type TFormScenarioActionPayload = Omit<
  TScenarioActionPayload,
  | EScenarioActionKeys.parametersList
  | EScenarioActionKeys.method
  | EScenarioActionKeys.headers
> & {
  [EScenarioActionKeys.parametersList]: TFormParameterListContent[]
  [EScenarioActionKeys.method]: EScenarioActionMethod | undefined
  [EScenarioActionKeys.headers]: { key: string; value: string }[]
  [EScenarioActionKeys.authHeader]: string
  [EScenarioActionKeys.apiKey]: string
}

export type TUpdateScenarioActionTitlePayload = {
  [EScenarioActionKeys.name]: string
}

export type TScenarioAction = {
  [EScenarioActionKeys.id]: string
} & TScenarioPayload

export type TScenarioActionProps = {
  [EScenarioActionKeys.id]: string
} & TScenarioActionPayload

export type TScenarioActionError = TGenericAPIError

export type TStepComponentProps = {
  mainTitle: string
  mainSubtitle: string
  onContinue: () => void
  onBack: () => void
  newScenarioId: string | null
  setNewScenarioId: (scenarioId: string | null) => void
}

export type TScenariosParams = {
  actionId?: string
  scenarioId?: string
}

export type TScenarioStepMetadata = {
  label: string
  formKey: ECreateEditScenarioFormKeys
}

export type TStepComponent =
  | MemoExoticComponent<() => JSX.Element>
  | (() => JSX.Element)

enum ETemplateStatus {
  active = 'active',
}

enum EScenarioType {
  toolCall = 'toolCall',
  liveTransfer = 'liveTransfer',
}

export type TScenarioTemplateAction = {
  id: string
  scenarioTemplateId: string
  name: string
  requiredAgentCustomFields?: Record<string, TAgentCustomField> | null
  requiredActionParametersList?: Record<string, TParameterListContent>
  createdDate: string
  updatedDate: string
  description?: string | null
  authHeader?: string | null
  endpointUrl?: string | null
  failureMessage?: string | null
  headers?: string | null
  httpMethod: EScenarioActionMethod
}

export type TScenarioTemplate = {
  additionalInstructions?: null
  availableChannels?: EConversationChannel[] | null
  createdDate: string
  id: string
  searchKeywords?: string[] | null
  status?: ETemplateStatus | null
  templateDescription?: string | null
  templateGroupName?: null
  templateName: string
  scenarioType?: EScenarioType | null
  stepPages?: TScenarioStepPages[] | null
  templateGroupDescription?: string | null
  triggerInstructions?: string | null
  updatedDate: string
  requiredAgentCustomFields?: Record<string, TAgentCustomField> | null
  templateInstructionSteps?: TInstructionStep[]
  scenarioTemplateActions?: TScenarioTemplateAction[] | null
}

export type TScenarioTemplateGroup = {
  count: number
  searchKeywords?: string[] | null
  templateGroupDescription: string
  templateGroupName: string
  templates: TScenarioTemplate[]
}

export enum ECreateEditScenarioFormKeys {
  triggerInstructionsStep = 'triggerInstructionsStep',
  actionsStep = 'actionsStep',
  advancedConfigurationStep = 'advancedConfigurationStep',
  activeForAgentsStep = 'activeForAgentsStep',
  reviewStep = 'reviewStep',
}

export type TFormAgentCustomFieldWithUuid = TFormAgentCustomField & {
  uuid: string
}

export type TActiveForAgentsStep = {
  [EScenarioAgentKeys.active]?: boolean
  [EScenarioAgentKeys.customFields]: {
    [x: string]: string | number | boolean
  }
}

export type TFormHeaderListContent = {
  id: string
  key: string
  value: string
}

export type TScenarioNewAction = {
  [EScenarioActionKeys.id]: string
  [EScenarioActionKeys.name]: string
  [EScenarioActionKeys.method]?: EScenarioActionMethod
  [EScenarioActionKeys.webhook]?: string
  [EScenarioActionKeys.apiActionDescription]?: string
  [EScenarioActionKeys.failureMessage]?: string
  [EScenarioActionKeys.waitMessage]?: string
  [EScenarioActionKeys.parametersList]?: TFormParameterListContent[]
  [EScenarioActionKeys.headers]?: TFormHeaderListContent[]
  [EScenarioActionKeys.scenarioTemplateActionId]?: string
  [EScenarioActionKeys.connectionId]: string | null
}

export enum EActionStepType {
  INSTRUCTION = 'instruction',
  API_CALL = 'apiCall',
}

export type TActionStepBase = {
  id: string
  type: EActionStepType
  description: string
  [EScenarioActionKeys.actionIds]?: string[]
}

export type TActionStep = TActionStepBase & {
  newAction?: TScenarioNewAction
}

export type TCreateEditScenarioForm = {
  [ECreateEditScenarioFormKeys.triggerInstructionsStep]: {
    [EScenarioKeys.name]: string
    [EScenarioKeys.triggerInstructions]: string
    [EScenarioKeys.channels]: EChannelKeys[]
  }
  [ECreateEditScenarioFormKeys.actionsStep]: {
    actionIds: string[]
    steps: TActionStep[]
  }
  [ECreateEditScenarioFormKeys.advancedConfigurationStep]: {
    [EScenarioKeys.additionalInstructions]: string
    [EScenarioKeys.agentCustomFields]: TFormAgentCustomFieldWithUuid[]
  }
  [ECreateEditScenarioFormKeys.activeForAgentsStep]: Record<
    string,
    TActiveForAgentsStep
  >
}

export type TCreateEditScenarioActionForm = {
  [EActionKeys.name]: string
  [EActionKeys.httpMethod]: EScenarioActionMethod
  [EActionKeys.webhook]: string
  [EActionKeys.description]: string
  [EActionKeys.failureMessage]: string
  [EActionKeys.waitMessage]: string
  [EActionKeys.parametersList]: TFormParameterListContent[]
  [EActionKeys.headers]: TFormHeaderListContent[]
  [EActionKeys.updatedDate]: string
  [EActionKeys.connectionId]: string | null
}

export type TCreateScenarioFromTemplatePayload = {
  inputs?: Record<string, string>
  apiKeys?: Record<string, string>
}

export enum EScenarioCreateModalSteps {
  GROUP_SELECTION = 'group-selection',
  TEMPLATE_SELECTION = 'template-selection',
  CUSTOM_PAGES = 'custom-pages',
}

export type TScenarioCreateModalState = {
  currentStep: EScenarioCreateModalSteps
  isCustomSelected: boolean
  searchQuery: string
  selectedGroupTemplate: TScenarioTemplateGroup | null
  selectedTemplate: TScenarioTemplate | null
  stepPages: TScenarioStepPages[] | null
}

export enum EScenarioCreateModalActions {
  SET_SEARCH = 'SET_SEARCH',
  SELECT_GROUP = 'SELECT_GROUP',
  SELECT_TEMPLATE = 'SELECT_TEMPLATE',
  SELECT_CURRENT_STEP = 'SELECT_CURRENT_STEP',
  SELECT_CUSTOM = 'SELECT_CUSTOM',
  CUSTOM_PAGES = 'CUSTOM_PAGES',
  GO_BACK = 'GO_BACK',
  RESET = 'RESET',
}

export type TScenarioCreateModalAction =
  | { type: EScenarioCreateModalActions.SET_SEARCH; payload: string }
  | {
      type: EScenarioCreateModalActions.SELECT_GROUP
      payload: TScenarioTemplateGroup
    }
  | {
      type: EScenarioCreateModalActions.SELECT_TEMPLATE
      payload: TScenarioTemplate
    }
  | { type: EScenarioCreateModalActions.SELECT_CUSTOM }
  | {
      type: EScenarioCreateModalActions.CUSTOM_PAGES
      payload: TScenarioStepPages[]
    }
  | {
      type: EScenarioCreateModalActions.SELECT_CURRENT_STEP
      payload: EScenarioCreateModalSteps
    }
  | { type: EScenarioCreateModalActions.GO_BACK }
  | { type: EScenarioCreateModalActions.RESET }

export type TScenarioStepPagesForm = {
  [EScenarioStepPagesKeys.cal]?: {
    [EScenarioCalStepPageKeys.apiKey]: string
    [EScenarioCalStepPageKeys.eventTypeId]: string
  }
}

export enum EScenarioStepSubmenuType {
  addBefore = 'add-before',
  addAfter = 'add-after',
  changeTo = 'change-to',
}

export enum EAuthStrategyType {
  apiKey = 'apiKey',
  tokenExchange = 'tokenExchange',
  oauth2 = 'oauth2',
}

export enum EConnectionStatus {
  pending = 'pending',
  connected = 'connected',
  error = 'error',
  expired = 'expired',
}

export enum EIntegrationProvider {
  abcIgnite = 'abcIgnite',
  custom = 'custom',
  mindbody = 'mindbody',
  hubspot = 'hubspot',
}

export enum EConnectFormFieldKeys {
  default = 'default',
  type = 'type',
  required = 'required',
  label = 'label',
  secret = 'secret',
  description = 'description',
}

export type TConnectFormField = {
  [EConnectFormFieldKeys.type]: 'string' | 'number' | 'boolean' | 'array'
  [EConnectFormFieldKeys.required]: boolean
  [EConnectFormFieldKeys.label]: string
  [EConnectFormFieldKeys.default]?: string | number | boolean
  [EConnectFormFieldKeys.secret]?: boolean
  [EConnectFormFieldKeys.description]?: string
}

export type TConnectFormSchema = Record<string, TConnectFormField>

export type TConnectFormValues = Record<
  string,
  string | number | boolean | null
>

export enum EAddConnectionFormKeys {
  provider = 'provider',
  displayName = 'displayName',
  input = 'input',
}

export type TAddConnectionForm = {
  [EAddConnectionFormKeys.provider]: string
  [EAddConnectionFormKeys.displayName]: string
  [EAddConnectionFormKeys.input]: TConnectFormValues
}

export type TCreateConnectionPayload = {
  [EAddConnectionFormKeys.provider]: string
  [EAddConnectionFormKeys.displayName]?: string
  [EAddConnectionFormKeys.input]?: Record<string, string>
  scopes?: TCreateConnectionScopePayload[]
}

export type TUpdateConnectionPayload = {
  [EAddConnectionFormKeys.displayName]?: string
  [EAddConnectionFormKeys.input]?: Record<string, string>
}

export type TOauthAuthorizePayload = {
  returnUrl: string
  displayName?: string
}

export type TOauthAuthorizeResponse = {
  url: string
}

export type TOauthConnectionMessage = {
  connectionId: string | null
  error: string | null
  type: 'oauth-connection-result'
}

export enum EIntegrationCatalogKeys {
  id = 'id',
  displayName = 'displayName',
  logoUrl = 'logoUrl',
  authStrategy = 'authStrategy',
  connectFormSchema = 'connectFormSchema',
  capabilities = 'capabilities',
}

export type TIntegrationCatalogEntry = {
  [EIntegrationCatalogKeys.id]: string
  [EIntegrationCatalogKeys.displayName]: string
  [EIntegrationCatalogKeys.logoUrl]?: string
  [EIntegrationCatalogKeys.authStrategy]: EAuthStrategyType
  [EIntegrationCatalogKeys.connectFormSchema]: TConnectFormSchema
  [EIntegrationCatalogKeys.capabilities]: string[]
}

export enum EConnectionKeys {
  id = 'id',
  orgId = 'orgId',
  provider = 'provider',
  authStrategy = 'authStrategy',
  displayName = 'displayName',
  status = 'status',
  config = 'config',
  expiresAt = 'expiresAt',
  lastValidatedAt = 'lastValidatedAt',
  lastError = 'lastError',
  connectedByCognitoId = 'connectedByCognitoId',
  createdDate = 'createdDate',
  updatedDate = 'updatedDate',
}

export type TConnection = {
  [EConnectionKeys.id]: string
  [EConnectionKeys.orgId]: string
  [EConnectionKeys.provider]: string
  [EConnectionKeys.authStrategy]: EAuthStrategyType
  [EConnectionKeys.displayName]: string | null
  [EConnectionKeys.status]: EConnectionStatus
  [EConnectionKeys.config]: Record<string, unknown> | null
  [EConnectionKeys.expiresAt]: string | null
  [EConnectionKeys.lastValidatedAt]: string | null
  [EConnectionKeys.lastError]: string | null
  [EConnectionKeys.connectedByCognitoId]: string | null
  [EConnectionKeys.createdDate]: string
  [EConnectionKeys.updatedDate]: string
}

export enum EConnectionScopeStatus {
  active = 'active',
  error = 'error',
  pending = 'pending',
}

export enum EConnectionScopeKeys {
  activatedAt = 'activatedAt',
  agentId = 'agentId',
  displayName = 'displayName',
  id = 'id',
  scopeValue = 'scopeValue',
  siteId = 'siteId',
  status = 'status',
}

export type TConnectionScope = {
  [EConnectionScopeKeys.id]: string
  [EConnectionScopeKeys.agentId]: string
  [EConnectionScopeKeys.scopeValue]: string
  [EConnectionScopeKeys.siteId]: string
  [EConnectionScopeKeys.displayName]: string | null
  [EConnectionScopeKeys.status]: EConnectionScopeStatus | null
  [EConnectionScopeKeys.activatedAt]: string | null
}

export type TCreateConnectionScopePayload = Pick<
  TConnectionScope,
  EConnectionScopeKeys.agentId | EConnectionScopeKeys.scopeValue
> &
  Partial<Pick<TConnectionScope, EConnectionScopeKeys.displayName>>

export enum EConnectionSelectorOptionType {
  app = 'app',
  connection = 'connection',
}

export enum EConnectionSelectorGroup {
  apps = 'Apps',
  noResults = 'no-results',
  yourConnections = 'Your Connections',
}

export enum EConnectionSelectorOptionKeys {
  description = 'description',
  displayName = 'displayName',
  group = 'group',
  id = 'id',
  logoUrl = 'logoUrl',
  provider = 'provider',
  type = 'type',
}

export type TConnectionSelectorOption = {
  [EConnectionSelectorOptionKeys.description]: string
  [EConnectionSelectorOptionKeys.displayName]: string
  [EConnectionSelectorOptionKeys.group]: EConnectionSelectorGroup
  [EConnectionSelectorOptionKeys.id]: string
  [EConnectionSelectorOptionKeys.logoUrl]?: string
  [EConnectionSelectorOptionKeys.provider]: string
  [EConnectionSelectorOptionKeys.type]: EConnectionSelectorOptionType
}
